import Icon from 'components/AppIcon';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useAddBankDetailMutation,
  useDeleteBankDetailMutation,
  useGetBankDetailByUserIdQuery,
  useSetAsPrimaryBankDetailMutation,
  useUpdateBankDetailMutation,
} from 'redux/features/bankDetailsApi';
import { toast } from 'react-toastify';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import BankDetailsDisplay from './components/BankDetailsDisplay';
import AddBankDetailModal from './components/AddBankDetailModal';

const BankDetailsTab = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams?.get('id');
  const [showAddBankDetailModal, setShowAddBankDetailModal] = useState(false);
  const [bankDetailToEdit, setBankDetailToEdit] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [bankDetailIdToDelete, setBankDetailIdToDelete] = useState(null);

  const { data } = useGetBankDetailByUserIdQuery(parseInt(userId), {
    skip: !parseInt(userId),
  });
  const [addBankDetail, { isLoading: isAddingBankDetail }] = useAddBankDetailMutation();
  const [updateBankDetail, { isLoading: isUpdatingBankDetail }] = useUpdateBankDetailMutation();
  const [setAsPrimaryBankDetail, { isLoading: isSettingAsPrimaryBankDetail }] =
    useSetAsPrimaryBankDetailMutation();
  const [deleteBankDetail, { isLoading: isDeletingBankDetail }] = useDeleteBankDetailMutation();
  const handleAddBankDetail = async (bankDetail) => {
    try {
      await addBankDetail({ ...bankDetail, user_id: parseInt(userId) }).unwrap();
      toast.success('Bank detail added successfully');
      setShowAddBankDetailModal(false);
    } catch {
      setShowAddBankDetailModal(false);
    }
    handleClose();
  };

  const handleSetAsPrimaryBankDetail = async (bankDetailId) => {
    try {
      await setAsPrimaryBankDetail({ id: parseInt(userId), bankDetailId }).unwrap();
      toast.success('Bank detail set as primary successfully');
    } catch {
      console.error('Failed to set bank detail as primary');
    }
    handleClose();
  };

  const handleEditBankDetail = (bankDetail) => {
    setShowAddBankDetailModal(true);
    setBankDetailToEdit(bankDetail);
  };
  const handleUpdateBankDetail = async (bankDetail) => {
    try {
      await updateBankDetail({ id: bankDetailToEdit.id, ...bankDetail }).unwrap();
      toast.success('Bank detail updated successfully');
      setShowAddBankDetailModal(false);
    } catch {
      setShowAddBankDetailModal(false);
    }
    handleClose();
  };
  const handleClose = () => {
    setShowAddBankDetailModal(false);
    setBankDetailToEdit(null);
  };

  const handleDeleteBankDetail = async () => {
    try {
      await deleteBankDetail({ id: bankDetailIdToDelete }).unwrap();
      toast.success('Bank detail deleted successfully');
      setConfirmationModal(false);
      setBankDetailIdToDelete(null);
    } catch {
      toast.error('Failed to delete bank detail');
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={confirmationModal}
        onClose={() => setConfirmationModal(false)}
        onConfirm={() => handleDeleteBankDetail(handleDeleteBankDetail)}
        title="Delete Bank Detail"
        message="Are you sure you want to delete this bank detail?"
        isLoading={isDeletingBankDetail}
      />
      <AddBankDetailModal
        data={bankDetailToEdit}
        onSubmit={handleAddBankDetail}
        onUpdate={handleUpdateBankDetail}
        isOpen={showAddBankDetailModal}
        onClose={handleClose}
        isSubmitting={isAddingBankDetail || isUpdatingBankDetail}
      />
      <div className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Icon name="Banknote" size={20} color="var(--color-primary)" />
              <span>Bank Details</span>
            </h2>
            <button
              onClick={() => setShowAddBankDetailModal(true)}
              title="Add Bank Detail"
              className="bg-primary rounded-lg text-white px-4 py-2 hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <Icon name="Plus" size={20} color="white" />
            </button>
          </div>
          <BankDetailsDisplay
            onDelete={(bankDetailId) => {
              setConfirmationModal(true);
              setBankDetailIdToDelete(bankDetailId);
            }}
            onEdit={handleEditBankDetail}
            isSettingAsPrimaryBankDetail={isSettingAsPrimaryBankDetail}
            handleSetAsPrimaryBankDetail={handleSetAsPrimaryBankDetail}
            bankDetailsData={data}
            onAddNew={() => setShowAddBankDetailModal(true)}
          />
        </div>
      </div>
    </>
  );
};

export default BankDetailsTab;
